import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

const pillars = [
  'Planification automatique des fauteuils',
  'Checklist de consultation en direct',
  'Alertes de retard sans surcharge visuelle',
  'Synthèse clinique en fin de journée',
];

const capabilities = [
  {
    tag: 'SYS_AGENDA',
    title: 'Orchestration du temps',
    desc: 'Chaque créneau est assigné, chaque fauteuil optimisé. Zéro temps mort, zéro conflit.',
    color: '#22d3ee',
  },
  {
    tag: 'SYS_DOSSIER',
    title: 'Dossiers en temps réel',
    desc: 'Historique clinique, radios, notes — tout converge dans un flux unique au fauteuil.',
    color: '#84cc16',
  },
  {
    tag: 'SYS_FACTURE',
    title: 'Facturation intégrée',
    desc: 'Devis, actes, relances automatiques. Le financier suit le clinique sans friction.',
    color: '#f59e0b',
  },
  {
    tag: 'SYS_MULTI',
    title: 'Multi-site unifié',
    desc: 'Pilotez plusieurs cabinets depuis un seul cockpit. Vision globale, action locale.',
    color: '#a78bfa',
  },
];

const timeline = [
  { time: '08:30', event: 'Ouverture fauteuil A — M. Benali', status: 'OK', statusColor: '#84cc16' },
  { time: '09:00', event: 'Checklist pré-op validée', status: 'OK', statusColor: '#84cc16' },
  { time: '09:15', event: 'Patient Mme. Idrissi — Détartrage', status: 'ACTIF', statusColor: '#22d3ee' },
  { time: '09:45', event: 'Alerte retard fauteuil B (+8min)', status: 'WARN', statusColor: '#f59e0b' },
  { time: '10:00', event: 'M. Lahlou — Prothèse céramique', status: 'QUEUE', statusColor: '#94a3b8' },
  { time: '10:30', event: 'Facture #1247 générée auto', status: 'OK', statusColor: '#84cc16' },
];

export default function LandingV4() {
  return (
    <div
      className="doctif-page"
      style={{
        background:
          'radial-gradient(circle at 82% 18%, rgba(8,145,178,0.18), transparent 34%), radial-gradient(circle at 16% 82%, rgba(132,204,22,0.18), transparent 38%), var(--doctif-indigo)',
        color: '#e2e8f0',
        fontFamily: 'IBM Plex Mono, monospace',
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>doctif — Control Room</title>
      </Helmet>

      {/* ── Header ── */}
      <header className="doctif-shell" style={{ padding: '22px 0', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <strong style={{ fontFamily: 'Archivo Black, sans-serif', fontSize: 32, color: '#f8fafc' }}>doctif</strong>
          <div className="doctif-chip-row">
            <a className="doctif-chip" href="/1" style={{ background: '#1e293b', color: '#bae6fd' }}>V1</a>
            <a className="doctif-chip" href="/2" style={{ background: '#1e293b', color: '#bae6fd' }}>V2</a>
            <a className="doctif-chip" href="/3" style={{ background: '#1e293b', color: '#bae6fd' }}>V3</a>
            <a className="doctif-chip" href="/4" style={{ background: '#06b6d4', color: '#082f49' }}>V4</a>
          </div>
        </div>
      </header>

      {/* ── Hero Section ── */}
      <main className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: '0.95fr 1.05fr', gap: 24, position: 'relative', zIndex: 2, paddingBottom: 40 }}>
        <section style={{ alignSelf: 'center' }}>
          <p className="doctif-kicker doctif-fade-up" style={{ color: '#67e8f9' }}>Ops cockpit pour cabinets dentaires</p>
          <h1
            className="doctif-fade-up doctif-delay-1"
            style={{
              fontFamily: 'Archivo Black, sans-serif',
              fontSize: 'clamp(2.1rem, 8vw, 5rem)',
              lineHeight: 0.94,
              marginTop: 16,
              marginBottom: 16,
              color: '#f8fafc',
              textTransform: 'uppercase',
            }}
          >
            Control Room
            <br />
            by doctif
          </h1>
          <p className="doctif-fade-up doctif-delay-2" style={{ maxWidth: 560, color: '#cbd5e1', lineHeight: 1.65 }}>
            Une landing au style raw-tech: blocs nets, contraste fort, repère visuel immédiat.
            Chaque action importante reste visible et exploitable en moins de trois secondes.
          </p>

          <div className="doctif-fade-up doctif-delay-3" style={{ display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
            <a
              href="/accounts/login/"
              style={{
                background: '#84cc16',
                color: '#0f172a',
                borderRadius: 12,
                padding: '12px 18px',
                fontWeight: 800,
                textDecoration: 'none',
                border: '2px solid #a3e635',
              }}
            >
              Entrer dans doctif
            </a>
            <a
              href="/booking"
              style={{
                background: 'transparent',
                color: '#67e8f9',
                borderRadius: 12,
                padding: '12px 18px',
                fontWeight: 800,
                textDecoration: 'none',
                border: '2px solid #155e75',
              }}
            >
              Test patient
            </a>
          </div>
        </section>

        <section className="doctif-fade-up doctif-delay-2" style={{ display: 'grid', gap: 10 }}>
          {pillars.map((pill, idx) => (
            <article
              key={pill}
              style={{
                borderRadius: 14,
                border: '1px solid #334155',
                padding: '14px 16px',
                background: idx % 2 === 0 ? 'rgba(15, 23, 42, 0.72)' : 'rgba(12, 74, 110, 0.35)',
                animation: idx === 0 ? 'doctifPulseBorder 2.2s ease infinite' : 'none',
              }}
            >
              <div style={{ color: '#22d3ee', fontSize: 11, marginBottom: 6 }}>MODULE_{idx + 1}</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{pill}</div>
            </article>
          ))}

          <div
            style={{
              marginTop: 6,
              borderRadius: 16,
              background: 'linear-gradient(115deg, rgba(8,47,73,0.85), rgba(6,182,212,0.18))',
              border: '1px solid #164e63',
              padding: 16,
            }}
          >
            <div style={{ color: '#67e8f9', fontSize: 11, marginBottom: 8 }}>LIVE_METRICS</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                ['97%', 'ponctualité'],
                ['11m', 'attente moy.'],
                ['4.9', 'score patient'],
              ].map(([value, label]) => (
                <div key={label} style={{ borderRadius: 10, background: 'rgba(15,23,42,0.8)', padding: '10px 8px' }}>
                  <strong style={{ fontFamily: 'Archivo Black, sans-serif', fontSize: 20 }}>{value}</strong>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Decorative floating squares */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          width: 220,
          height: 220,
          border: '2px solid rgba(103,232,249,0.25)',
          top: 120,
          left: -80,
          transform: 'rotate(23deg)',
          animation: 'doctifFloat 4s ease-in-out infinite',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          width: 260,
          height: 260,
          border: '2px dashed rgba(132,204,22,0.24)',
          bottom: -70,
          right: -40,
          transform: 'rotate(-18deg)',
          animation: 'doctifFloat 5s ease-in-out infinite',
        }}
      />

      {/* ── Stats Bar ── */}
      <section style={{
        borderTop: '1px solid #1e293b',
        borderBottom: '1px solid #1e293b',
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(8px)',
        position: 'relative',
        zIndex: 3,
      }}>
        <div className="doctif-shell" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          padding: '32px 0',
          gap: 0,
        }}>
          {[
            { value: '240+', label: 'CABINETS', sub: 'connectés' },
            { value: '61,247', label: 'RDV/MOIS', sub: 'traités' },
            { value: '99.8%', label: 'UPTIME', sub: 'garanti' },
            { value: '<3s', label: 'LATENCE', sub: 'moyenne' },
          ].map((s, i) => (
            <div key={s.label} className={`doctif-fade-up doctif-delay-${i + 1}`} style={{
              textAlign: 'center',
              borderRight: i < 3 ? '1px solid #1e293b' : 'none',
              padding: '0 16px',
            }}>
              <div style={{ fontFamily: 'Archivo Black, sans-serif', fontSize: 36, color: '#22d3ee', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#67e8f9', marginTop: 8, letterSpacing: '0.15em', fontWeight: 700 }}>{s.label}</div>
              <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Capabilities Grid ── */}
      <section className="doctif-shell" style={{ padding: '80px 0 60px', position: 'relative', zIndex: 3 }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ color: '#67e8f9', fontSize: 11, letterSpacing: '0.2em', fontWeight: 700, marginBottom: 12 }}>SYSTEM_MODULES</div>
          <h2 style={{
            fontFamily: 'Archivo Black, sans-serif',
            fontSize: 'clamp(1.8rem, 5vw, 3.2rem)',
            color: '#f8fafc',
            textTransform: 'uppercase',
            lineHeight: 1,
            marginBottom: 12,
          }}>
            Architecture
            <span style={{ color: '#84cc16' }}> modulaire</span>
          </h2>
          <p style={{ color: '#64748b', maxWidth: 520, lineHeight: 1.65-0 }}>
            Chaque sous-système opère indépendamment. Activez uniquement ce dont votre cabinet a besoin.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 16,
        }}>
          {capabilities.map((cap, i) => (
            <article
              key={cap.tag}
              className={`doctif-fade-up doctif-delay-${Math.min(i + 1, 4)}`}
              style={{
                borderRadius: 16,
                border: '1px solid #1e293b',
                padding: '24px 22px',
                background: 'rgba(15, 23, 42, 0.5)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color 0.3s, transform 0.2s',
                cursor: 'default',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = cap.color + '66';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = '#1e293b';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              }}
            >
              {/* Corner accent */}
              <div aria-hidden style={{
                position: 'absolute', top: 0, left: 0,
                width: 3, height: 40,
                background: cap.color,
                borderRadius: '0 0 4px 0',
              }} />
              <div style={{ color: cap.color, fontSize: 11, letterSpacing: '0.12em', fontWeight: 700, marginBottom: 10 }}>{cap.tag}</div>
              <h3 style={{ fontFamily: 'Archivo Black, sans-serif', fontSize: 20, color: '#f1f5f9', marginBottom: 8 }}>{cap.title}</h3>
              <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6 }}>{cap.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Live Feed / Dashboard Preview ── */}
      <section className="doctif-shell" style={{ paddingBottom: 80, position: 'relative', zIndex: 3 }}>
        <div style={{
          borderRadius: 20,
          border: '1px solid #1e293b',
          background: 'linear-gradient(165deg, rgba(15,23,42,0.9), rgba(2,6,23,0.95))',
          overflow: 'hidden',
          boxShadow: '0 40px 80px -20px rgba(0,0,0,0.6)',
        }}>
          {/* Terminal-style header bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
            borderBottom: '1px solid #1e293b',
            background: 'rgba(15,23,42,0.8)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#84cc16' }} />
              </div>
              <span style={{ color: '#475569', fontSize: 12 }}>doctif://control-room/live-feed</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['AGENDA', 'PATIENTS', 'BILLING'].map((tab, i) => (
                <span key={tab} style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  background: i === 0 ? '#22d3ee' : 'transparent',
                  color: i === 0 ? '#082f49' : '#475569',
                  border: i === 0 ? 'none' : '1px solid #1e293b',
                }}>{tab}</span>
              ))}
            </div>
          </div>

          {/* Feed content */}
          <div style={{ padding: '20px 20px 24px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}>
              {/* Left: Timeline */}
              <div>
                <div style={{ color: '#67e8f9', fontSize: 11, letterSpacing: '0.12em', fontWeight: 700, marginBottom: 14 }}>EVENT_LOG</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {timeline.map((entry) => (
                    <div key={entry.time + entry.event} style={{
                      display: 'grid',
                      gridTemplateColumns: '52px 1fr auto',
                      gap: 10,
                      alignItems: 'center',
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: 'rgba(15,23,42,0.6)',
                      border: '1px solid #0f172a',
                      fontSize: 13,
                    }}>
                      <span style={{ color: '#475569', fontWeight: 600 }}>{entry.time}</span>
                      <span style={{ color: '#cbd5e1' }}>{entry.event}</span>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: entry.statusColor,
                        padding: '2px 8px',
                        borderRadius: 4,
                        background: entry.statusColor + '18',
                        letterSpacing: '0.08em',
                      }}>{entry.status}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Live Stats Panel */}
              <div>
                <div style={{ color: '#67e8f9', fontSize: 11, letterSpacing: '0.12em', fontWeight: 700, marginBottom: 14 }}>REALTIME_DASH</div>
                <div style={{
                  borderRadius: 14,
                  border: '1px solid #1e293b',
                  background: 'rgba(2,6,23,0.7)',
                  padding: 18,
                }}>
                  {/* Mini chart bars */}
                  <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 12 }}>Charge fauteuils — aujourd'hui</div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 80, marginBottom: 16 }}>
                    {[65, 85, 92, 78, 95, 88, 70, 60, 45, 30, 0, 0].map((pct, i) => (
                      <div key={i} style={{
                        flex: 1,
                        height: `${pct}%`,
                        background: pct > 85 ? '#22d3ee' : pct > 50 ? '#0e7490' : '#164e63',
                        borderRadius: '3px 3px 0 0',
                        minHeight: pct > 0 ? 4 : 0,
                        transition: 'height 0.6s ease',
                      }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#475569' }}>
                    <span>08h</span><span>10h</span><span>12h</span><span>14h</span><span>16h</span><span>18h</span>
                  </div>

                  {/* Summary row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 8,
                    marginTop: 18,
                    paddingTop: 16,
                    borderTop: '1px solid #1e293b',
                  }}>
                    {[
                      ['12', 'RDV restants'],
                      ['3/4', 'Fauteuils actifs'],
                      ['€2,840', 'CA du jour'],
                    ].map(([val, lbl]) => (
                      <div key={lbl}>
                        <div style={{ fontFamily: 'Archivo Black, sans-serif', fontSize: 18, color: '#f1f5f9' }}>{val}</div>
                        <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{lbl}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status indicators */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                  {[
                    { label: 'API', status: 'ONLINE', color: '#84cc16' },
                    { label: 'DB', status: 'HEALTHY', color: '#84cc16' },
                    { label: 'SYNC', status: 'ACTIVE', color: '#22d3ee' },
                    { label: 'BACKUP', status: '02:00', color: '#f59e0b' },
                  ].map(s => (
                    <div key={s.label} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: 'rgba(15,23,42,0.6)',
                      border: '1px solid #0f172a',
                      fontSize: 11,
                    }}>
                      <span style={{ color: '#64748b' }}>{s.label}</span>
                      <span style={{ color: s.color, fontWeight: 700, letterSpacing: '0.06em' }}>
                        <span style={{
                          display: 'inline-block',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: s.color,
                          marginRight: 6,
                          animation: s.color === '#84cc16' ? 'doctifPulse 2s ease infinite' : 'none',
                        }} />
                        {s.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Workflow Section ── */}
      <section style={{
        borderTop: '1px solid #1e293b',
        background: 'rgba(2,6,23,0.5)',
        position: 'relative',
        zIndex: 3,
      }}>
        <div className="doctif-shell" style={{ padding: '80px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ color: '#84cc16', fontSize: 11, letterSpacing: '0.2em', fontWeight: 700, marginBottom: 12 }}>WORKFLOW_ENGINE</div>
            <h2 style={{
              fontFamily: 'Archivo Black, sans-serif',
              fontSize: 'clamp(1.6rem, 4vw, 2.8rem)',
              color: '#f8fafc',
              textTransform: 'uppercase',
              lineHeight: 1.1,
            }}>
              Du premier patient<br />au dernier
              <span style={{ color: '#22d3ee' }}> rapport</span>
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
          }}>
            {[
              {
                step: '01',
                title: 'INTAKE',
                desc: 'Le patient réserve en ligne. Formulaire pré-rempli, confirmation SMS automatique.',
                icon: '→',
                accent: '#22d3ee',
              },
              {
                step: '02',
                title: 'OPERATE',
                desc: 'Checklist interactive au fauteuil. Notes vocales, photos, actes codifiés en direct.',
                icon: '⚡',
                accent: '#84cc16',
              },
              {
                step: '03',
                title: 'CLOSE',
                desc: 'Facture générée, relance programmée, synthèse quotidienne envoyée au praticien.',
                icon: '✓',
                accent: '#f59e0b',
              },
            ].map((w) => (
              <div key={w.step} style={{
                padding: '32px 24px',
                background: 'rgba(15,23,42,0.5)',
                border: '1px solid #1e293b',
                borderRadius: 16,
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Top accent bar */}
                <div aria-hidden style={{
                  position: 'absolute', top: 0, left: 0, right: 0,
                  height: 2,
                  background: `linear-gradient(90deg, ${w.accent}, transparent)`,
                }} />
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 18,
                }}>
                  <span style={{
                    fontFamily: 'Archivo Black, sans-serif',
                    fontSize: 36,
                    color: w.accent + '33',
                    lineHeight: 1,
                  }}>{w.step}</span>
                  <span style={{
                    fontSize: 24,
                    width: 44,
                    height: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 12,
                    background: w.accent + '15',
                    border: `1px solid ${w.accent}33`,
                  }}>{w.icon}</span>
                </div>
                <h3 style={{
                  fontFamily: 'Archivo Black, sans-serif',
                  fontSize: 18,
                  color: w.accent,
                  marginBottom: 10,
                  letterSpacing: '0.08em',
                }}>{w.title}</h3>
                <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6 }}>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonial / Social Proof ── */}
      <section className="doctif-shell" style={{ padding: '60px 0', position: 'relative', zIndex: 3 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}>
          <div style={{
            borderRadius: 18,
            border: '1px solid #1e293b',
            background: 'rgba(15,23,42,0.5)',
            padding: '28px 24px',
          }}>
            <div style={{ color: '#22d3ee', fontSize: 11, letterSpacing: '0.12em', fontWeight: 700, marginBottom: 16 }}>SIGNAL_USER</div>
            <blockquote style={{
              fontFamily: 'Archivo Black, sans-serif',
              fontSize: 20,
              color: '#f1f5f9',
              lineHeight: 1.4,
              margin: 0,
              marginBottom: 18,
            }}>
              "On a réduit notre temps administratif de 40%. L'équipe ne veut plus revenir en arrière."
            </blockquote>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0e7490, #164e63)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: '#67e8f9',
              }}>DK</div>
              <div>
                <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 700 }}>Dr. Khalil</div>
                <div style={{ color: '#475569', fontSize: 11 }}>Cabinet Dentaire Al Madina — Casablanca</div>
              </div>
            </div>
          </div>

          <div style={{
            borderRadius: 18,
            border: '1px solid #1e293b',
            background: 'rgba(15,23,42,0.5)',
            padding: '28px 24px',
          }}>
            <div style={{ color: '#84cc16', fontSize: 11, letterSpacing: '0.12em', fontWeight: 700, marginBottom: 16 }}>SIGNAL_USER</div>
            <blockquote style={{
              fontFamily: 'Archivo Black, sans-serif',
              fontSize: 20,
              color: '#f1f5f9',
              lineHeight: 1.4,
              margin: 0,
              marginBottom: 18,
            }}>
              "La vue multi-cabinet a changé notre façon de piloter. Tout est là, en temps réel."
            </blockquote>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'linear-gradient(135deg, #365314, #4d7c0f)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: '#a3e635',
              }}>SB</div>
              <div>
                <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 700 }}>Dr. Brahmi</div>
                <div style={{ color: '#475569', fontSize: 11 }}>Clinique Dentaire Corbei — Oujda</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        borderTop: '1px solid #1e293b',
        position: 'relative',
        zIndex: 3,
        background: 'linear-gradient(180deg, rgba(2,6,23,0.5) 0%, rgba(8,145,178,0.08) 100%)',
      }}>
        <div className="doctif-shell" style={{ padding: '80px 0', textAlign: 'center' }}>
          <div style={{ color: '#22d3ee', fontSize: 11, letterSpacing: '0.2em', fontWeight: 700, marginBottom: 16 }}>READY_TO_DEPLOY</div>
          <h2 style={{
            fontFamily: 'Archivo Black, sans-serif',
            fontSize: 'clamp(1.8rem, 5vw, 3.5rem)',
            color: '#f8fafc',
            textTransform: 'uppercase',
            lineHeight: 1.05,
            marginBottom: 16,
          }}>
            Votre cabinet.
            <br />
            <span style={{ color: '#84cc16' }}>Notre infrastructure.</span>
          </h2>
          <p style={{ color: '#64748b', maxWidth: 480, margin: '0 auto 32px', lineHeight: 1.65, fontSize: 15 }}>
            Déploiement en 48h. Migration assistée. Support dédié.
            <br />Sans engagement. Sans carte bancaire.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="/accounts/login/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                background: '#84cc16',
                color: '#0f172a',
                padding: '16px 32px',
                borderRadius: 14,
                fontWeight: 800,
                fontSize: 16,
                textDecoration: 'none',
                border: '2px solid #a3e635',
                boxShadow: '0 12px 40px rgba(132,204,22,0.25)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
            >
              Démarrer maintenant
              <span style={{ fontSize: 20 }}>→</span>
            </a>
            <a
              href="/booking"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'transparent',
                color: '#67e8f9',
                padding: '16px 28px',
                borderRadius: 14,
                fontWeight: 700,
                fontSize: 16,
                textDecoration: 'none',
                border: '2px solid #155e75',
                transition: 'border-color 0.3s',
              }}
            >
              Voir la démo
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ borderTop: '1px solid #0f172a', position: 'relative', zIndex: 3 }}>
        <div className="doctif-shell" style={{
          padding: '24px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ fontFamily: 'Archivo Black, sans-serif', fontSize: 18, color: '#334155' }}>doctif</strong>
            <span style={{ color: '#1e293b', fontSize: 12 }}>|</span>
            <span style={{ color: '#334155', fontSize: 11 }}>control room v4.0</span>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            {['Confidentialité', 'CGU', 'Contact'].map(link => (
              <a key={link} href="#" style={{ color: '#334155', fontSize: 11, textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#67e8f9')}
                onMouseLeave={e => (e.currentTarget.style.color = '#334155')}
              >{link}</a>
            ))}
          </div>
        </div>
      </footer>

      {/* Responsive overrides */}
      <style>{`
        @media (max-width: 980px) {
          main.doctif-shell { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 768px) {
          .doctif-shell > div[style*='grid-template-columns: repeat(4'] { grid-template-columns: repeat(2, 1fr) !important; }
          .doctif-shell > div[style*='grid-template-columns: repeat(2'] { grid-template-columns: 1fr !important; }
          .doctif-shell > div[style*='grid-template-columns: repeat(3'] { grid-template-columns: 1fr !important; }
          .doctif-shell > div[style*='grid-template-columns: 1fr 1fr'] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
